import React, { useState, useEffect } from 'react';
import {
  AppShell,
  Container,
  Group,
  ActionIcon,
  Button,
  Modal,
  Title,
  Text,
  Anchor,
  Box,
  Center,
  Loader,
  useMantineColorScheme,
  useComputedColorScheme,
} from '@mantine/core';
import { Moon, Sun, Lock } from 'lucide-react';
import { Switch, Route, useLocation } from 'wouter';
import Home from './pages/Home';
import Docs from './pages/Docs';
import Pricing from './pages/Pricing';
import Setup from './pages/Setup';
import Login from './pages/Login';

function ThemeToggle() {
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');
  const isDark = computedColorScheme === 'dark';

  return (
    <ActionIcon
      variant="subtle"
      color="gray"
      size="lg"
      onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
      aria-label="Toggle theme"
    >
      {isDark ? <Sun size={20} /> : <Moon size={20} />}
    </ActionIcon>
  );
}

function HeaderNav() {
  const [location, setLocation] = useLocation();
  const isDocs = location === '/docs';
  const isPricing = location === '/pricing';

  return (
    <AppShell.Header>
      <Container size="xl" h="100%">
        <Group h="100%" justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <img src="/favicon.svg" alt="Py2Native logo" width={30} height={30} style={{ display: 'block' }} />
            <Button
              variant="transparent"
              component="a"
              onClick={() => setLocation('/')}
              p={0}
              styles={{ label: { fontSize: 'inherit' } }}
            >
              <Text fz={24} fw={900} ff="inherit" lh={1}>
                <Text span c="blue" inherit>Py</Text>
                <Text span inherit>2</Text>
                <Text span c="blue" inherit>Native</Text>
              </Text>
            </Button>
          </Group>
          <Group gap="xl" wrap="nowrap">
            <Group gap="xl" wrap="nowrap">
              <Button
                variant="subtle"
                onClick={() => setLocation('/docs')}
                c={isDocs ? 'blue' : undefined}
                fw={600}
              >
                Docs
              </Button>
              <Button
                variant="subtle"
                onClick={() => setLocation('/pricing')}
                c={isPricing ? 'blue' : undefined}
                fw={600}
              >
                Pricing
              </Button>
              <Button
                variant="subtle"
                component="a"
                href="/blog/index.html"
                fw={600}
              >
                Blog
              </Button>
            </Group>
            <ThemeToggle />
          </Group>
        </Group>
      </Container>
    </AppShell.Header>
  );
}

function Footer({ onAdminClick }) {
  const [showImprint, setShowImprint] = useState(false);

  return (
    <>
      <Box
        py="xs"
        style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
      >
        <Container size="xl">
          <Group justify="center" gap="xs">
            <Anchor
              component="button"
              onClick={() => setShowImprint(true)}
              size="sm"
              c="dimmed"
              td="underline"
            >
              Imprint / Legal Notice
            </Anchor>
            {onAdminClick && (
              <>
                <Text span size="sm" c="dimmed">·</Text>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={onAdminClick}
                  aria-label="Admin"
                  title="Admin"
                >
                  <Lock size={14} />
                </ActionIcon>
              </>
            )}
            <Text span size="sm" c="dimmed">·</Text>
            <Text span size="sm" c="dimmed">
              © {new Date().getFullYear()} Py2Native (RSJ Software GmbH). All rights reserved.
            </Text>
          </Group>
        </Container>
      </Box>

      <Modal
        opened={showImprint}
        onClose={() => setShowImprint(false)}
        title="Imprint (Legal Notice)"
        size="md"
      >
        <Text mb="md">
          <Text span fw={700}>Company Information</Text><br />
          RSJ Software GmbH<br />
          Holzstrasse 4<br />
          82110 Germering<br />
          Germany
        </Text>
        <Text mb="md">
          <Text span fw={700}>Contact</Text><br />
          Phone: +49 89 894142-0<br />
          Fax: +49 89 894142-80<br />
          E-Mail: <Anchor href="mailto:info@rsj.de">info@rsj.de</Anchor><br />
          Internet: <Anchor href="https://www.rsj.de" target="_blank" rel="noopener noreferrer">https://www.rsj.de</Anchor>
        </Text>
        <Text mb="md">
          <Text span fw={700}>Registration & Taxation</Text><br />
          VAT-ID: DE 128 239 592<br />
          Tax ID: 117/136/80100<br />
          Trade Register: Munich HRB 76084
        </Text>
        <Text>
          <Text span fw={700}>Management</Text><br />
          Managing Director: Ruediger Jungbeck
        </Text>
      </Modal>
    </>
  );
}

export default function App() {
  const [needsSetup, setNeedsSetup] = useState(null); // null = loading, true = show setup, false = normal
  const [clientSetup, setClientSetup] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [runtimeSecret, setRuntimeSecret] = useState('');
  const [, setLocation] = useLocation();

  useEffect(() => {
    fetch('/api/config')
      .then((res) => res.json())
      .then((data) => {
        setNeedsSetup(data.needsSetup === true);
        setClientSetup(data);
      })
      .catch(() => setNeedsSetup(false));
  }, []);

  if (needsSetup === null) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // Unconfigured: show setup directly (no login required)
  if (needsSetup) {
    return (
      <AppShell
        header={{ height: 70 }}
        padding={0}
      >
        <HeaderNav />
        <AppShell.Main>
          <Setup />
          <Footer />
        </AppShell.Main>
      </AppShell>
    );
  }

  // Configured + admin authenticated: show setup for reconfiguration
  if (isAdmin) {
    return (
      <AppShell
        header={{ height: 70 }}
        padding={0}
      >
        <HeaderNav />
        <AppShell.Main>
          <Setup runtimeSecret={runtimeSecret} />
          <Footer onAdminClick={() => { setIsAdmin(false); setRuntimeSecret(''); }} />
        </AppShell.Main>
      </AppShell>
    );
  }

  // Normal app (configured, not admin)
  return (
    <AppShell
      header={{ height: 70 }}
      padding={0}
    >
      <HeaderNav />
      <AppShell.Main>
        <Switch>
          <Route path="/">
            <Home />
          </Route>
          <Route path="/docs">
            <Docs />
          </Route>
          <Route path="/pricing">
            <Pricing
              proPrice={clientSetup.proPrice}
              enterprisePrice={clientSetup.enterprisePrice}
              authServer={clientSetup.authServer}
            />
          </Route>
          <Route path="/admin">
            <Login onSuccess={(secret) => { setRuntimeSecret(secret); setIsAdmin(true); }} />
          </Route>
        </Switch>
        <Footer onAdminClick={() => setLocation('/admin')} />
      </AppShell.Main>
    </AppShell>
  );
}
